import { AlertCircle, Inbox, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAllRunsQuery, useFlowsQuery } from '@/api/hooks.js';
import { EmptyState } from '@/components/EmptyState.js';
import { Button } from '@/components/ui/button.js';
import { Card, CardContent } from '@/components/ui/card.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { RunStatusBadge } from '@/lib/status.js';
import { formatDuration, formatRelative } from '@/lib/time.js';

export function RunsPage() {
  const navigate = useNavigate();
  const flowsQuery = useFlowsQuery();
  const runs = useAllRunsQuery();

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Runs</h1>
          <p className="text-sm text-muted-foreground">
            Recent runs across {flowsQuery.data?.length ?? 0} flow
            {flowsQuery.data?.length === 1 ? '' : 's'}.
            {runs.hasActiveRun ? ' Polling every 30s while runs are active.' : ''}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void runs.refetch()}
          disabled={runs.isLoading || runs.isFetching}
        >
          <RefreshCw className={runs.isFetching ? 'mr-1 h-3.5 w-3.5 animate-spin' : 'mr-1 h-3.5 w-3.5'} />
          Refresh
        </Button>
      </div>

      {runs.isError ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-6 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            Failed to load runs.
          </CardContent>
        </Card>
      ) : null}

      {!runs.isLoading && runs.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={Inbox}
            title="No runs yet"
            description="Trigger a flow from the Flows tab to create one."
          />
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>Flow</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Data rows</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.isLoading ? <SkeletonRows /> : null}

              {runs.data.map((run) => (
                <TableRow
                  key={run.id}
                  onClick={() => navigate(`/runs/${run.id}`)}
                  className="cursor-pointer"
                >
                  <TableCell className="font-medium">{formatRelative(run.startedAt)}</TableCell>
                  <TableCell>{run.flowName}</TableCell>
                  <TableCell>
                    <RunStatusBadge status={run.status} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {run.triggerSource}
                  </TableCell>
                  <TableCell className="text-sm">{formatDuration(run.durationMs)}</TableCell>
                  <TableCell className="text-sm">
                    {formatRowCount(run.rowCount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function formatRowCount(rowCount: number | null | undefined): string {
  if (rowCount == null) return '—';
  return String(rowCount);
}

function SkeletonRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <TableRow key={i}>
          {[0, 1, 2, 3, 4, 5].map((j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-3/4" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
